import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import type { AuthSession, SessionSnapshot } from '../features/auth/types';
import { apiClient } from './client';

/**
 * `/api/v1/auth` — design.md §4.1.
 *
 * Credentials travel in the request body only, never a query string, header
 * or URL (Story H). Nothing here logs a submitted value.
 */

/** The session query key. Role mutations invalidate it — see design.md §5.4. */
export const authMeQueryKey = ['auth', 'me'] as const;

export type LoginInput = {
  email: string;
  password: string;
};

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
};

export function fetchSession(signal?: AbortSignal): Promise<SessionSnapshot> {
  return apiClient.get<SessionSnapshot>('/auth/me', { signal });
}

export function logout(): Promise<void> {
  return apiClient.post<void>('/auth/logout');
}

/**
 * bcrypt at cost 12 is ~250ms server-side and the login page must never look
 * hung, so the request gets a longer budget than the client default.
 */
const CREDENTIAL_TIMEOUT_MS = 30_000;

export function useLoginMutation(): UseMutationResult<AuthSession, Error, LoginInput> {
  return useMutation({
    mutationFn: (input: LoginInput) =>
      apiClient.post<AuthSession>('/auth/login', {
        body: input,
        timeoutMs: CREDENTIAL_TIMEOUT_MS,
      }),
  });
}

export function useChangePasswordMutation(): UseMutationResult<
  AuthSession,
  Error,
  ChangePasswordInput
> {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      apiClient.post<AuthSession>('/auth/change-password', {
        body: input,
        timeoutMs: CREDENTIAL_TIMEOUT_MS,
      }),
  });
}
